/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */
import './GetPromotedCard.scss';

import React, { Fragment } from 'react';

import LinkButton from 'components/shared/Buttons/LinkButton';
import {
  preferencesLinks,
  transferExternalAsset,
  leverageHouseholdAssets
} from 'services/Externals';
import { Dollar } from 'components/shared/Text/Text';
import { programBenefitsContent } from 'services/Content';
import MobileBenefitsDropdown from './MobileBenefitsDropdown';
import { LinkRpsAccountsCard } from './LinkRpsAccountsCard';
import {
  PREFERRED,
  SELECT,
  PERSONAL_SERVICES,
  ENHANCED_PERSONAL_SERVICES,
  summitMembershipMapById,
  PS_MIN_AMOUNT
} from 'services/HouseholdInformationCard';
import { ReactComponent as Play } from 'assets/icons/play.svg';
import { ReactComponent as ShareBack } from 'assets/icons/share-back.svg';
import { ReactComponent as MutualFund } from 'assets/icons/mutual-fund.svg';
import { ReactComponent as Book } from 'assets/icons/book.svg';
import { updatedSummitBenefitsEnabled } from 'services/FeatureFlags';
import { WHOLE_DOLLAR } from 'services/Formats';

const GrowBalanceTiles = ({
  showRPSTile,
  hasRpsInfoError,
  subscribed,
  shouldDisable
}) => {
  return (
    <Fragment>
      <div className="grow-balance-container">
        <div className="grow-balance-card">
          <p>Transfer External Assets</p>
          <div className="grow-balance-description">
            Increase your Summit balance by transferring investments you
            currently hold elsewhere.
          </div>
          <LinkButton link={transferExternalAsset}>Start Transfer</LinkButton>
        </div>
        <div className="grow-balance-card">
          <p>Set Up Recurring Transactions</p>
          <div className="grow-balance-description">
            Contribute to your accounts regularly to increase your Summit
            balance.
          </div>
          <LinkButton link={preferencesLinks.automaticTransactionsLink}>
            Start Recurring Transaction
          </LinkButton>
        </div>
        <div className="grow-balance-card">
          <p>Leverage Household Assets</p>
          <div className="grow-balance-description">
            Householding allows you to access additional benefits based on your
            household’s Summit balance.
          </div>
          <LinkButton link={leverageHouseholdAssets}>Learn More</LinkButton>
        </div>
        {showRPSTile && !hasRpsInfoError && (
          <div className="grow-balance-card long">
            <LinkRpsAccountsCard
              subscribed={subscribed}
              shouldDisable={shouldDisable}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

const MobileNextTierBenefits = ({ nextTierId }) => {
  return (
    <Fragment>
      {nextTierId === PREFERRED && (
        <MobileBenefitsDropdown
          title="T. Rowe Price Premium Content"
          description={programBenefitsContent.trowePremiumContent}
        />
      )}
      {nextTierId === SELECT && (
        <div>
          <MobileBenefitsDropdown
            title="Closed Funds"
            description={programBenefitsContent.closedFunds}
          />
          <MobileBenefitsDropdown
            title="Morningstar®"
            description={programBenefitsContent.morningStar}
          />
        </div>
      )}
      {nextTierId === PERSONAL_SERVICES && (
        <MobileBenefitsDropdown
          title="I Class shares"
          description={programBenefitsContent.iClassShares}
        />
      )}
      {nextTierId === ENHANCED_PERSONAL_SERVICES && (
        <div>
          <MobileBenefitsDropdown
            title="Kiplinger’s Personal Finance Magazine"
            description={programBenefitsContent.kiplingerPersonalMagazine}
          />
          <MobileBenefitsDropdown
            title="Exclusive Events"
            description={programBenefitsContent.exclusiveEvents}
          />
        </div>
      )}
    </Fragment>
  );
};

const NextTierBenefits = ({ nextTierId }) => {
  return (
    <Fragment>
      {nextTierId === PREFERRED && (
        <div className="next-tier-benefits">
          <Book className="book-svg" />
          <div>
            <b>T. Rowe Price Premium Content</b>
            <p>{programBenefitsContent.trowePremiumContent}</p>
          </div>
        </div>
      )}
      {nextTierId === SELECT && (
        <Fragment>
          <div className="next-tier-benefits">
            <MutualFund className="mutual-fund-svg" />
            <div>
              <b>Closed Funds</b>
              <p>{programBenefitsContent.closedFunds}</p>
            </div>
          </div>
          <div className="next-tier-benefits">
            <Book className="book-svg" />
            <div>
              <b>Morningstar®</b>
              <p>{programBenefitsContent.morningStar}</p>
            </div>
          </div>
        </Fragment>
      )}
      {nextTierId === PERSONAL_SERVICES && (
        <div className="next-tier-benefits">
          <ShareBack className="share-back-svg" />
          <div>
            <b>I Class shares</b>
            <p>{programBenefitsContent.iClassShares}</p>
          </div>
        </div>
      )}
      {nextTierId === ENHANCED_PERSONAL_SERVICES && (
        <Fragment>
          <div className="next-tier-benefits">
            <Book className="book-svg" />
            <div>
              <b>Kiplinger’s Personal Finance Magazine</b>
              <p>{programBenefitsContent.kiplingerPersonalMagazine}</p>
            </div>
          </div>
          <div className="next-tier-benefits">
            <Play />
            <div>
              <b>Exclusive Events</b>
              <p>{programBenefitsContent.exclusiveEvents}</p>
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

const GetPromotedCard = ({
  nextTierId,
  nextTierMinAmount,
  isMobileLayout,
  showRPSTile,
  hasRpsInfoError,
  subscribed,
  shouldDisable
}) => {
  return (
    <div className="get-promoted-container">
      <div className="get-promoted-container-content">
        <p className="benefits-page-title">
          <b>
            Upgrade to {summitMembershipMapById[nextTierId]} status and access
            these benefits:
          </b>
        </p>
        <div className="get-promoted-subtext">
          A Summit balance of{' '}
          <Dollar format={updatedSummitBenefitsEnabled() ? WHOLE_DOLLAR : "$0,0.00"}>{nextTierMinAmount}</Dollar> or more{' '}
          {nextTierMinAmount === PS_MIN_AMOUNT ? (
            <span>
              {' '}
              and an investment minimum of <Dollar format={WHOLE_DOLLAR}>
                50000
              </Dollar>{' '}
              per qualifying fund account{' '}
            </span>
          ) : null}{' '}
          is required.
        </div>
        <div className="next-tier-benefits-container">
          {isMobileLayout ? (
            <MobileNextTierBenefits nextTierId={nextTierId} />
          ) : (
            <NextTierBenefits nextTierId={nextTierId} />
          )}
        </div>
        <p className="benefits-page-title">
          You can grow your qualifying Summit balance in a variety of ways.
        </p>
        <GrowBalanceTiles
          showRPSTile={showRPSTile}
          hasRpsInfoError={hasRpsInfoError}
          subscribed={subscribed}
          shouldDisable={shouldDisable}
        />
        <p className="benefits-page-paragraph">
          Eligibility for promotion to the next tier is determined in processes
          that generally take up to 10 days to complete. Benefits associated
          with your new tier will be available to you at that time.
        </p>
      </div>
    </div>
  );
};

export default GetPromotedCard;
